import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/useAuthStore";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Verifica de forma síncrona se existe token Supabase no localStorage.
 * Permite redirecionar visitantes anônimos imediatamente para /auth,
 * sem mostrar tela de loading enquanto o Zustand inicializa a sessão.
 */
function hasStoredSupabaseSession(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID as
      | string
      | undefined;
    if (projectId) {
      const v = window.localStorage.getItem(`sb-${projectId}-auth-token`);
      if (v) return true;
    }
    // Fallback: varre qualquer chave sb-*-auth-token (cobre URLs alternativas).
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && /^sb-.*-auth-token$/.test(k)) return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * ProtectedRoute — Redireciona para /auth se não autenticado.
 *
 * UX rules:
 *  - Se já temos `isAuthenticated`, renderiza children.
 *  - Se ainda está carregando MAS não há token salvo, redireciona imediatamente
 *    (visitante anônimo nunca vê spinner full-screen).
 *  - Só mostra spinner quando há sessão salva e estamos hidratando — janela
 *    geralmente de poucos ms.
 */
export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated, loading } = useAuthStore();
  const location = useLocation();

  if (loading) {
    if (!hasStoredSupabaseSession()) {
      return <Navigate to="/auth" state={{ from: location }} replace />;
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <span className="text-sm text-muted-foreground">
            Verificando sessão...
          </span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};
